import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Card, Table, Button, Switch, Modal, Form, Divider,
  Input, InputNumber, Row, Col, Typography, notification, Tooltip, Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, BankOutlined } from '@ant-design/icons';
import { baseUrl } from '../../config';
import { clearPaymentDropdownCache } from '../../hooks/usePaymentDropdowns';

const { Title, Text } = Typography;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

export default function BankAccountPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [saving, setSaving]     = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/bank-account/list`, { headers: authHeaders() });
      if (res.data.status) setAccounts(res.data.result || []);
    } catch (err) {
      notification.error({ message: 'Failed to load accounts: ' + err.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (rec) => { setEditing(rec); form.setFieldsValue(rec); setModalOpen(true); };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${baseUrl}/bank-account/${editing.id}`, values, { headers: authHeaders() });
        notification.success({ message: 'Account updated.' });
      } else {
        await axios.post(`${baseUrl}/bank-account`, values, { headers: authHeaders() });
        notification.success({ message: 'Account created.' });
      }
      setModalOpen(false);
      clearPaymentDropdownCache();
      load();
    } catch (err) {
      notification.error({ message: err.response?.data?.message || err.message });
    } finally { setSaving(false); }
  };

  const handleToggle = async (rec) => {
    try {
      const res = await axios.patch(`${baseUrl}/bank-account/${rec.id}/toggle`, {}, { headers: authHeaders() });
      clearPaymentDropdownCache();
      setAccounts(prev => prev.map(a => a.id === rec.id ? { ...a, is_active: res.data.is_active } : a));
    } catch (err) {
      notification.error({ message: 'Toggle failed: ' + err.message });
    }
  };

  const columns = [
    { title: '#', dataIndex: 'id', key: 'id', width: 40, fixed: 'left' },
    {
      title: 'Account Name', dataIndex: 'account_name', key: 'account_name', width: 220, fixed: 'left',
      render: (v, rec) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{v}</Text>
          {rec.org_name && <div style={{ fontSize: 11, color: '#888' }}>{rec.org_name}</div>}
        </div>
      ),
    },
    { title: 'Bank', dataIndex: 'bank_name', key: 'bank_name', width: 70, render: v => v || '-' },
    {
      title: 'Org / PAN', key: 'org_info', width: 160,
      render: (_, rec) => (
        <div style={{ fontSize: 11 }}>
          {rec.pan_no && <div><Text type="secondary">PAN:</Text> {rec.pan_no}</div>}
          {rec.reg_no && <div><Text type="secondary">Reg:</Text> {rec.reg_no}</div>}
          {rec.receipt_contact && <div style={{ color: '#888' }}>{rec.receipt_contact}</div>}
        </div>
      ),
    },
    {
      title: 'Receipt Prefix', dataIndex: 'receipt_prefix', key: 'receipt_prefix', width: 110,
      render: v => v === null || v === undefined
        ? <Tag color="default" style={{ fontSize: 11 }}>No Receipts</Tag>
        : v === '' ? <Tag color="blue" style={{ fontSize: 11 }}>None (SN)</Tag>
        : <Tag color="green" style={{ fontSize: 11 }}>{v}-</Tag>,
    },
    {
      title: 'Poster / WA Trigger', key: 'triggers', width: 160,
      render: (_, rec) => (
        <div style={{ fontSize: 11 }}>
          {rec.poster_trigger_key
            ? <div><Text type="secondary">Poster:</Text> <Tag color="purple" style={{ fontSize: 10 }}>{rec.poster_trigger_key}</Tag></div>
            : <div style={{ color: '#ccc' }}>-</div>}
          {rec.wa_event_key
            ? <div><Text type="secondary">WA:</Text> <Tag color="cyan" style={{ fontSize: 10 }}>{rec.wa_event_key}</Tag></div>
            : null}
        </div>
      ),
    },
    { title: 'Sort', dataIndex: 'sort_order', key: 'sort_order', width: 50 },
    {
      title: 'Status', key: 'status', width: 100,
      render: (_, rec) => (
        <Switch
          checked={rec.is_active === 1}
          onChange={() => handleToggle(rec)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: 'Action', key: 'action', width: 70, fixed: 'right',
      render: (_, rec) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(rec)}>Edit</Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <BankOutlined style={{ marginRight: 8 }} />Bank Accounts
          </Title>
          <Text type="secondary">Manage payment accounts shown in Add Payment</Text>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} style={{ background: '#7a0000' }}>
            Add Account
          </Button>
        </Col>
      </Row>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          dataSource={accounts}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ x: 900 }}
        />
      </Card>

      <Modal
        title={editing ? 'Edit Account' : 'Add Account'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText={editing ? 'Update' : 'Create'}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" size="small">

          {/* ── Basic Info ── */}
          <Form.Item label="Account Name" name="account_name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. ABRSVS(SHIKSHA NIDHI)-9078(SBI)" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Account Number" name="account_no">
                <Input placeholder="Account number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Bank Name" name="bank_name">
                <Input placeholder="e.g. SBI" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Sort Order" name="sort_order">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
          </Form.Item>

          {/* ── Receipt Details ── */}
          <Divider orientation="left" style={{ fontSize: 13, color: '#555' }}>Receipt Details</Divider>
          <Form.Item label="Organization Name (shown on receipt)" name="org_name">
            <Input placeholder="e.g. अखिल भारतीय राजपुरोहित समाज..." />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="PAN No" name="pan_no">
                <Input placeholder="e.g. AAATA2501N" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Registration No" name="reg_no">
                <Input placeholder="e.g. 57/1997-98/22-12-1997" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Contact Numbers (shown on receipt)" name="receipt_contact">
            <Input placeholder="e.g. 7023131008, 9030301008" />
          </Form.Item>
          <Form.Item
            label={
              <Tooltip title="Empty string '' = no prefix (Shiksha Nidhi). 'G' = Gaushala receipts become G-2025-26/1. Leave blank (null) = no receipt numbers generated.">
                Receipt No Prefix &nbsp;<span style={{ color: '#999', fontSize: 11 }}>(leave empty for SN, 'G' for Gaushala, blank = no receipts)</span>
              </Tooltip>
            }
            name="receipt_prefix"
          >
            <Input placeholder="e.g. G  (leave blank for Shiksha Nidhi, remove value entirely to disable receipts)" allowClear />
          </Form.Item>

          {/* ── Poster / WhatsApp ── */}
          <Divider orientation="left" style={{ fontSize: 13, color: '#555' }}>Poster & WhatsApp</Divider>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                label={<Tooltip title="Must match a trigger key in the Poster Config rules engine (e.g. payment_new, payment_gaushala)">Poster Trigger Key</Tooltip>}
                name="poster_trigger_key"
              >
                <Input placeholder="e.g. payment_gaushala" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<Tooltip title="Must match an event_key in WhatsApp Config (e.g. payment_gaushala)">WhatsApp Event Key</Tooltip>}
                name="wa_event_key"
              >
                <Input placeholder="e.g. payment_gaushala" />
              </Form.Item>
            </Col>
          </Row>

        </Form>
      </Modal>
    </div>
  );
}
